import React, { useState, useEffect } from 'react';
import { getSettings } from '../models/settingsModel';
import { API_BASE_URL } from '../config';
import { toast } from 'react-toastify';

const MigrationCard = ({ migration, formatDate }) => {
  const [showSql, setShowSql] = useState(false);
  
  return (
    <div className="p-6 hover:bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center">
            <h3 className="text-sm font-medium text-gray-900">{migration.migration_name}</h3>
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Migration
            </span>
          </div>
          {migration.update_description && (
            <div className="mt-1 text-sm text-gray-600" dangerouslySetInnerHTML={{ 
              __html: migration.update_description.length > 100 
                ? migration.update_description.substring(0, 100) + '...' 
                : migration.update_description 
            }} />
          )}
          <p className="mt-1 text-xs text-gray-400">
            Update ID: {migration.update_id} • Applied: {formatDate(migration.applied_at)}
          </p>
        </div>
        {migration.sql_content && (
          <button
            onClick={() => setShowSql(!showSql)}
            className="ml-4 text-sm text-blue-600 hover:text-blue-800"
          >
            {showSql ? 'Hide SQL' : 'View SQL'}
          </button>
        )}
      </div>
      {showSql && migration.sql_content && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto whitespace-pre-wrap">
            {migration.sql_content}
          </pre>
        </div>
      )}
    </div>
  );
};

const UpdateChecker = () => {
  const [updates, setUpdates] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [installing, setInstalling] = useState({});
  const [rolling, setRolling] = useState({});
  const [systemStatus, setSystemStatus] = useState('healthy');
  const [activeTab, setActiveTab] = useState('updates');

  const compareVersions = (version1, version2) => {
    const v1 = version1.replace(/Portal\.v/, '').split('.').map(Number);
    const v2 = version2.replace(/Portal\.v/, '').split('.').map(Number);
    
    for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
      const num1 = v1[i] || 0;
      const num2 = v2[i] || 0;
      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }
    return 0;
  };

  const fetchUpdates = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/updates/check`);
      const data = await response.json();
      if (data.status === 1) {
        // Handle the new API response structure
        if (data.data.update_available && data.data.latest_update) {
          // Convert single update to array format
          const latestUpdate = data.data.latest_update;
          
          // Get the highest installed version
          const installedVersions = installed
            .filter(i => i.version)
            .map(i => i.version)
            .sort((a, b) => compareVersions(b, a));
          
          const latestInstalled = installedVersions[0];
          
          // Check if latest update is already installed
          let availableUpdates = [];
          if (!latestInstalled || compareVersions(latestUpdate.version, latestInstalled) > 0) {
            availableUpdates = [latestUpdate];
          }
          
          setUpdates(availableUpdates);
        } else {
          // Fallback to old format or no updates
          const allUpdates = data.data.updates || [];
          
          // Get the highest installed version
          const installedVersions = installed
            .filter(i => i.version)
            .map(i => i.version)
            .sort((a, b) => compareVersions(b, a));
          
          const latestInstalled = installedVersions[0];
          
          // Check if latest update is already installed
          const availableUpdates = allUpdates.filter(update => {
            if (!latestInstalled) return true;
            return compareVersions(update.version, latestInstalled) > 0;
          });
          
          setUpdates(availableUpdates);
        }
      } else {
        toast.error(data.message || 'Failed to check for updates');
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
      toast.error('Error checking for updates');
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstalled = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/updates/history`);
      const data = await response.json();
      if (data.status === 1) {
        setInstalled(data.data.updates || data.data.history || []);
        setMigrations(data.data.migrations || []);
      }
    } catch (error) {
      console.error('Error fetching installed updates:', error);
    }
  };

  useEffect(() => {
    fetchInstalled();
    
    // Check for success message after reload
    const successMessage = sessionStorage.getItem('updateSuccessMessage');
    if (successMessage) {
      toast.success(successMessage);
      sessionStorage.removeItem('updateSuccessMessage');
    }
  }, []);

  useEffect(() => {
    if (installed.length >= 0) {
      fetchUpdates();
    }
  }, [installed]);

  const installUpdate = async (updateId) => {
    setInstalling(prev => ({ ...prev, [updateId]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/updates/install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ update_id: updateId })
      });
      const result = await response.json();
      
      if (result.status === 1) {
        // Store success message and reload
        sessionStorage.setItem('updateSuccessMessage', 'Update installed successfully! The application has been refreshed.');
        window.location.reload();
      } else {
        toast.error('Installation failed: ' + result.message);
      }
    } catch (error) {
      console.error('Install error:', error);
      toast.error('Installation failed');
    } finally {
      setInstalling(prev => ({ ...prev, [updateId]: false }));
    }
  };

  const rollbackUpdate = async (updateId) => {
    setRolling(prev => ({ ...prev, [updateId]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/updates/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ update_id: updateId })
      });
      const result = await response.json();
      
      if (result.status === 1) {
        toast.success('Update rolled back successfully');
        fetchInstalled();
        fetchUpdates(); // Refresh available updates
      } else {
        toast.error('Rollback failed: ' + result.message);
      }
    } catch (error) {
      console.error('Rollback error:', error);
      toast.error('Rollback failed');
    } finally {
      setRolling(prev => ({ ...prev, [updateId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">System Updates</h1>
                <p className="text-xs text-gray-500 mb-1">Manage software updates and system maintenance</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {updates.length} available • {installed.length} installed
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Status */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchUpdates}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh Updates"
                >
                  <svg className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? 'Checking...' : 'Check Updates'}
                </button>
              </div>
              
              {/* System Status */}
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">Healthy</div>
                  <div className="text-xs text-gray-500">System Status</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">{updates.length}</div>
                  <div className="text-xs text-gray-500">Available</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">{installed.length}</div>
                  <div className="text-xs text-gray-500">Updates</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-purple-600">{migrations.length}</div>
                  <div className="text-xs text-gray-500">Migrations</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Installation History - Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">Installation History</h2>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setActiveTab('updates')}
                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                        activeTab === 'updates'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Updates ({installed.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('migrations')}
                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                        activeTab === 'migrations'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Migrations ({migrations.length})
                    </button>
                  </div>
                </div>
              </div>
              
              {activeTab === 'updates' && (
                <div className="divide-y divide-gray-200">
                  {installed.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p>No updates installed yet</p>
                    </div>
                  ) : (
                    installed.map((install, index) => (
                      <div key={install.id} className="p-6 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h3 className="text-sm font-medium text-gray-900">
                                {install.version ? `Version ${install.version}` : `Update ID: ${install.update_id}`}
                              </h3>
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Installed
                              </span>
                            </div>
                            {install.description && (
                              <div className="mt-1 text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: install.description }} />
                            )}
                            <p className="mt-1 text-xs text-gray-400">Installed: {formatDate(install.installed_at)}</p>
                          </div>
                          {index === 0 && installed.length > 1 && (
                            <div className="ml-4">
                              <button
                                onClick={() => rollbackUpdate(install.update_id)}
                                disabled={rolling[install.update_id]}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {rolling[install.update_id] ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Rolling back...
                                  </>
                                ) : (
                                  <>
                                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Rollback
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {activeTab === 'migrations' && (
                <div className="divide-y divide-gray-200">
                  {migrations.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p>No migrations applied yet</p>
                    </div>
                  ) : (
                    migrations.map((migration) => (
                      <MigrationCard key={migration.id} migration={migration} formatDate={formatDate} />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Available Updates - Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Available Updates</h2>
              </div>
              
              {loading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Checking...</p>
                </div>
              ) : updates.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mt-2 text-sm">Up to date</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {updates.map((update) => (
                    <div key={update.id} className="p-6">
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900">Version {update.version}</h3>
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          New
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: update.description }} />
                      <p className="mt-1 text-xs text-gray-400">Released: {formatDate(update.released_at || update.created_at)}</p>
                      <button
                        onClick={() => installUpdate(update.id)}
                        disabled={installing[update.id]}
                        className="mt-3 w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {installing[update.id] ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Installing...
                          </>
                        ) : (
                          <>
                            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                            </svg>
                            Install
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateChecker;